import * as React from 'react';
import {
  Callout,
  PrimaryButton,
  mergeStyles,
  IStyleFunctionOrObject,
  ITheme,
  IStyle,
  styled,
  concatStyleSetsWithProps
} from 'office-ui-fabric-react';
import { defaultLocale } from '@fluidx/utilities';
import { getPrimaryButtonStyle } from '../styles/getPrimaryButtonStyle';
import { HostAppColors } from '../styles/HostAppColors';
import { ThemeColorPaletteShared } from '@fluidx/office-fluid-types';

export interface PreviewLabelCalloutProps {
  /** The colors of the hosting app */
  appColors: HostAppColors;

  /** The title of the callout */
  title: string;

  /** The first paragraph text content of the callout */
  firstPara: string;

  /** The second paragraph text content of the callout */
  secondPara?: string;

  /** The text for the getting started guide link that will be shown in the callout */
  gettingStartedText?: string;

  /** The text for the terms of use link that will be shown in the callout */
  termsOfUseText: string;

  /** The text for the privacy and cookies link that will be shown in the callout */
  privacyAndCookiesText: string;

  /** The text for the third party notices link that will be shown in the callout */
  thirdPartyNoticesText: string;

  /** The label for the button in the callout */
  buttonLabel: string;

  /** The element around which to position the callout */
  target: Element | null;

  /** Callback to invoke that will dismiss the callout */
  onDismiss: () => void;

  /** The locales used for localizing the links from the body */
  linksLocale?: string;
}

interface PreviewLabelLinkProps {
  /** The className used to style the link */
  className: string;

  /** hyperlink to navigate too */
  link: string;

  /** Alternate text to display for the link */
  linkText: string;
}

const PreviewLabelLink = React.memo(function (props: PreviewLabelLinkProps) {
  const { className, link, linkText } = props;
  return (
    <a className={className} href={link} target="_blank" rel="noopener noreferrer">
      {linkText}
    </a>
  );
});

export const PreviewLabelCallout = (props: PreviewLabelCalloutProps) => {
  const { onDismiss, target } = props;

  const windowBlurListener = (event: FocusEvent) => {
    if (target && !event.relatedTarget && onDismiss) {
      onDismiss();
      window.removeEventListener('blur', windowBlurListener);
    }
  };

  window.addEventListener('blur', windowBlurListener);

  return (
    <Callout onDismiss={onDismiss} target={target} setInitialFocus={true}>
      <PreviewBody {...props} onPrimaryButtonClick={onDismiss} />
    </Callout>
  );
};

export interface PreviewBodyProps {
  /** The colors of the hosting app */
  appColors: HostAppColors;

  /** The title of the body */
  title: string;

  /** The first paragraph text content of the body */
  firstPara: string;

  /** The second paragraph text content of the body */
  secondPara?: string;

  /** The text for the getting started guide link that will be shown in the callout */
  gettingStartedText?: string;

  /** The text for the terms of use link that will be shown in the body */
  termsOfUseText: string;

  /** The text for the privacy and cookies link that will be shown in the body */
  privacyAndCookiesText: string;

  /** The text for the third party notices link that will be shown in the body */
  thirdPartyNoticesText: string;

  /** The label for the button in the body */
  buttonLabel: string;

  /** Callback invoked when clicking on the primary button */
  onPrimaryButtonClick: () => void;

  styles?: IStyleFunctionOrObject<PreviewBodyStyleProps, PreviewBodyStyles>;

  theme?: ITheme;

  /** The locales used for localizing the links from the body */
  linksLocale?: string;
}

interface PreviewBodyStyles {
  /** Style of the element which wraps all of the contents */
  wrapper?: IStyle;

  /** Style of the title */
  title?: IStyle;

  /** Style of the text */
  text?: IStyle;

  /** Style of any links */
  link?: IStyle;

  /** Style of the footer section */
  footer?: IStyle;
}

interface PreviewBodyStyleProps extends Pick<PreviewBodyProps, 'theme' | 'appColors'> {}

function getPreviewBodyDefaultStyle(props: PreviewBodyStyleProps): PreviewBodyStyles {
  const { theme, appColors } = props;

  return {
    wrapper: {
      width: 356,
      padding: '12px 20px',
      outline: 'none'
    },
    title: {
      fontWeight: 600,
      fontSize: '20px'
    },
    text: {
      whiteSpace: 'pre-line',
      paddingTop: 16,
      color: theme ? theme.palette.neutralSecondary : ThemeColorPaletteShared.Gray180,
      fontSize: '14px'
    },
    link: {
      display: 'block',
      color: appColors.themePrimary,
      selectors: {
        ':hover': {
          color: appColors.themeDark
        },
        ':pressed': {
          color: appColors.themeDarker
        }
      }
    },
    footer: {
      display: 'flex',
      flexDirection: 'row-reverse'
    }
  };
}

export const PreviewBody = React.memo(
  styled(function PreviewBody(props: PreviewBodyProps) {
    const {
      appColors,
      title,
      onPrimaryButtonClick,
      firstPara,
      secondPara,
      gettingStartedText,
      termsOfUseText,
      privacyAndCookiesText,
      thirdPartyNoticesText,
      buttonLabel,
      styles,
      linksLocale
    } = props;

    const resolvedStyles = concatStyleSetsWithProps(props, styles);
    const wrapperClass = mergeStyles(resolvedStyles.wrapper);
    const titleClass = mergeStyles(resolvedStyles.title);
    const textContentClass = mergeStyles(resolvedStyles.text);
    const linkClass = mergeStyles(resolvedStyles.link);
    const footerClass = mergeStyles(resolvedStyles.footer);

    const locale = linksLocale ?? defaultLocale;

    return (
      <div
        className={wrapperClass}
        aria-labelledby="previewCalloutTitle previewCalloutFirstPara previewCalloutSecondPara"
        tabIndex={0}
      >
        <div className={titleClass} id="previewCalloutTitle" role="heading">
          {title}
        </div>
        <div className={textContentClass} id="previewCalloutFirstPara">
          {firstPara}
        </div>
        <br />

        {gettingStartedText && (
          <>
            <PreviewLabelLink
              className={linkClass}
              link={`https://support.microsoft.com/${locale}/office/get-started-with-fluid-preview-d05278db-b82b-4d1f-8523-cf0c9c2fb2df?ui=${locale}&rs=${locale}`}
              linkText={gettingStartedText}
            />
            <br />
          </>
        )}

        {secondPara && (
          <>
            <div className={textContentClass} id="previewCalloutSecondPara">
              {secondPara}
            </div>
            <br />
          </>
        )}

        <PreviewLabelLink
          className={linkClass}
          link={`https://www.microsoft.com/${locale}/licensing/product-licensing/products`}
          linkText={termsOfUseText}
        />
        <PreviewLabelLink
          className={linkClass}
          link="https://go.microsoft.com/fwlink/?LinkId=521839"
          linkText={privacyAndCookiesText}
        />
        <PreviewLabelLink
          className={linkClass}
          link="https://officefluidprodversionedcdn.azureedge.net/7401955/app/thirdPartyNotices.html"
          linkText={thirdPartyNoticesText}
        />

        <div className={footerClass}>
          <PrimaryButton className={getPrimaryButtonStyle(appColors)} onClick={onPrimaryButtonClick}>
            {buttonLabel}
          </PrimaryButton>
        </div>
      </div>
    );
  }, getPreviewBodyDefaultStyle)
);
